import React, { useState } from "react";
import Modal from "../Modal";
import OrbitalLoader from "../common/OrbitalLoader";
import { brandsService } from "../../services/brands";
import { getApiErrorMessage } from "../../utils/apiErrors";
import { useToast } from "../../contexts/ToastContext";

const REGENERATING_MESSAGES = [
  "Rewriting the script with your notes...",
  "Spinning up fresh dialogue...",
  "Baking a new script in the oven...",
  "Rehearsing the new take...",
];

/**
 * Modal to edit/regenerate a script. Full-script: prompt + regenerate.
 * Scene-scoped: structured for future; for now opens same flow (regenerate full script).
 */
const EditScriptModal = ({
  script,
  sceneIndex = null,
  scene = null,
  onClose,
  onSuccess,
}) => {
  const [prompt, setPrompt] = useState(script?.context?.user_prompt ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [regeneratingMessage] = useState(
    () => REGENERATING_MESSAGES[Math.floor(Math.random() * REGENERATING_MESSAGES.length)]
  );
  const { showToast } = useToast();

  const ideaHash = script?.idea_hash;
  const isSceneEdit = sceneIndex != null;

  const handleSubmit = () => {
    if (!ideaHash) return;
    setSubmitting(true);
    brandsService.createScript(
      {
        idea_hash: ideaHash,
        prompt: prompt.trim() || undefined,
      },
      (response) => {
        const newScript = response?.data;
        setSubmitting(false);
        if (newScript?.hash) {
          onSuccess?.(newScript);
          onClose?.();
        }
      },
      (err) => {
        setSubmitting(false);
        showToast(getApiErrorMessage(err, "Script regeneration failed. Please try again."), {
          type: "error",
        });
      }
    );
  };

  if (submitting) {
    return (
      <Modal onClose={null} maxWidth="max-w-lg" heightClass="h-auto">
        <div className="flex flex-col items-center justify-center py-12">
          <OrbitalLoader size={96} special text={regeneratingMessage} />
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} maxWidth="max-w-lg" heightClass="h-auto">
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-theme-text">
          {isSceneEdit ? "Edit scene" : "Edit script"}
        </h3>
        {isSceneEdit && (
          <p className="text-sm text-theme-text opacity-80">
            Scene-level editing is coming soon. For now you can regenerate the full script with updated direction below.
          </p>
        )}
        <div>
          <label htmlFor="edit-script-prompt" className="block text-sm font-medium text-theme-text opacity-90 mb-1">
            Direction
          </label>
          <textarea
            id="edit-script-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Additional direction for the script (tone, focus, etc.)"
            rows={4}
            className="w-full px-3 py-2 rounded border border-theme-secondary bg-theme-secondary text-theme-text text-sm placeholder-theme-text placeholder-opacity-50 resize-none focus:outline-none focus:ring-2 focus:ring-theme-primary"
          />
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded border border-theme-secondary text-theme-text text-sm hover:bg-theme-secondary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 rounded bg-theme-primary text-theme-button text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? "Regenerating…" : "Regenerate script"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default EditScriptModal;
